import React, { useState, useEffect } from "react";
import { MassTime, Devotion } from "../types";
import { contactInfo } from "../config/massSchedule";
import { apiService } from "../services/api";

const MassTimes: React.FC = () => {
  const [massTimes, setMassTimes] = useState<MassTime[]>([]);
  const [devotions, setDevotions] = useState<Devotion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load both mass times and devotions
      const [massData, devotionData] = await Promise.all([
        apiService.getMassTimes(`?t=${Date.now()}`),
        apiService.getDevotions(),
      ]);

      // Convert API format to component format and sort by day
      const convertedTimes: MassTime[] = massData.map((schedule) => ({
        day: schedule.day,
        times: schedule.times.split(", "),
        special:
          schedule.special
      }));

      // Sort mass times by day of week
      const dayOrder = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      convertedTimes.sort((a, b) => {
        const aIndex = dayOrder.findIndex((day) => a.day.includes(day));
        const bIndex = dayOrder.findIndex((day) => b.day.includes(day));
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      });

      setMassTimes(convertedTimes);
      setDevotions(devotionData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      // Fallback to default times
      setMassTimes([
        {
          day: "Sunday",
          times: ["10:30 AM"],
          special: "My Parish My Mission during 10:30 AM Mass",
        },
        { day: "Weekdays (except Monday)", times: ["10:00 AM"] },
        {
          day: "Holy Days of Obligation",
          times: ["10:00 AM"],
          special: "See newsletter for details",
        },
      ]);
      setDevotions([]);
    } finally {
      setLoading(false);
    }
  };

  // Group devotions by day
  const groupedDevotions = devotions.reduce(
    (acc, devotion) => {
      if (!acc[devotion.day]) acc[devotion.day] = [];
      acc[devotion.day].push(devotion);
      return acc;
    },
    {} as Record<string, Devotion[]>,
  );

  // Sort devotions within each day
  Object.keys(groupedDevotions).forEach((day) => {
    groupedDevotions[day].sort((a, b) =>
      (a.time || "").localeCompare(b.time || ""),
    );
  });

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Mass times...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white" aria-labelledby="mass-times-heading">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2
            id="mass-times-heading"
            className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4"
          >
            Mass Times
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Join us for the celebration of the Eucharist. All are welcome to
            worship with our community: Weekday Mass times are subject to change
            from time to time. Please check the newsletter for updates.
          </p>
        </div>

        {/* Mass Times - Clean List Layout */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-12">
          <div className="bg-church-blue px-6 py-4">
            <h3 className="text-xl font-semibold text-white">
              Weekly Schedule
            </h3>
          </div>

          <div className="divide-y divide-gray-200">
            {massTimes.map((schedule, index) => (
              <div
                key={index}
                className="px-6 py-5 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="mb-2 sm:mb-0">
                    <h4 className="text-lg font-semibold text-church-dark mb-1">
                      {schedule.day}
                    </h4>
                    {schedule.special && (
                      <p className="text-sm text-church-gold font-medium italic">
                        {schedule.special}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end sm:gap-1">
                    {schedule.times.map((time, timeIndex) => (
                      <span
                        key={timeIndex}
                        className="inline-block px-3 py-1 bg-church-light text-church-dark font-medium rounded-full text-sm sm:text-base"
                      >
                        {time}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Devotions Section */}
        {Object.keys(groupedDevotions).length > 0 && (
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
                Devotions
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Join us for these special devotions and prayer services
                throughout the week.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-church-blue px-6 py-4">
                <h3 className="text-xl font-semibold text-white">
                  Devotions & Prayer Services
                </h3>
              </div>

              <div className="divide-y divide-gray-200">
                {[
                  "Sunday",
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                ]
                  .filter((day) => groupedDevotions[day])
                  .map((day) => (
                    <div
                      key={day}
                      className="px-6 py-5 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                        <div className="mb-3 sm:mb-0 sm:flex-1">
                          <h4 className="text-lg font-semibold text-church-dark mb-2">
                            {day}
                          </h4>
                        </div>

                        <div className="sm:flex-1 sm:max-w-md">
                          <div className="space-y-2">
                            {groupedDevotions[day].map((devotion) => (
                              <div key={devotion.id} className="text-left">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                  <span className="text-base font-medium text-church-dark">
                                    {devotion.name}
                                  </span>
                                  {devotion.time && (
                                    <span className="inline-block px-2 py-1 bg-church-light text-church-dark font-medium rounded text-sm mt-1 sm:mt-0">
                                      {devotion.time}
                                    </span>
                                  )}
                                </div>
                                {devotion.note && (
                                  <p className="text-sm text-church-gold italic mt-1">
                                    {devotion.note}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Additional Information */}
        <div className="bg-church-blue rounded-lg p-6 text-white">
          <h3 className="text-xl font-semibold mb-6 text-center">
            Additional Information
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="text-center lg:text-left">
              <h4 className="font-semibold mb-3 text-church-gold text-lg">
                Confessions
              </h4>
              <div className="space-y-1">
                <p>Saturday: 10:30 AM - 11:00 AM</p>
                <p>Or by appointment any time</p>
              </div>
            </div>

            <div className="text-center lg:text-left">
              <h4 className="font-semibold mb-3 text-church-gold text-lg">
                Sacrament of the Sick
              </h4>
              <div className="space-y-1">
                <p>Contact Father Rejimon</p>
                <p>Available any time on request</p>
              </div>
            </div>

            <div className="text-center lg:text-left">
              <h4 className="font-semibold mb-3 text-church-gold text-lg">
                Baptism & Marriage
              </h4>
              <div className="space-y-1">
                <p>Baptism: Contact Father Rejimon Devasia</p>
                <p>Marriage: 6 months notice required</p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-blue-800 text-center">
            <p className="text-sm">
              For special liturgies, holidays, or schedule changes, please check
              our
              <a
                href="/calendar"
                className="text-church-gold hover:underline ml-1 font-medium"
              >
                calendar
              </a>{" "}
              or contact {contactInfo.priestName}.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MassTimes;
